import type { DatasetPayload } from "../dataset.ts";

/**
 * Descarga de las filas de un dataset, 100% en el cliente.
 *
 * El dataset ya llegó por `useDataset` (React Query, `staleTime: Infinity`):
 * no se vuelve a pedir nada, solo se empaqueta lo que ya está en memoria.
 */

const triggerDownload = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Dar tiempo al navegador a tomar la descarga antes de invalidar la URL.
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 1000);
};

const cellOf = (value: string | number | boolean | null): string | number => {
  if (value === null) return "";
  if (typeof value === "boolean") return value ? "Sí" : "No";
  return value;
};

/** Escapa una celda para CSV: comillas dobladas si lleva coma, comilla o salto. */
const csvCell = (value: string | number): string => {
  const text = String(value);
  if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

/**
 * CSV a mano, con BOM UTF-8 al principio para que Excel abra bien los
 * acentos (sin el BOM, Excel asume Latin-1 y "participación" sale mal).
 */
export const downloadCsv = (
  dataset: DatasetPayload,
  fileName: string,
): void => {
  const header = dataset.columns.map((column) => csvCell(column.label));
  const lines = dataset.rows.map((row) =>
    dataset.columns.map((column) => csvCell(cellOf(row[column.key] ?? null))),
  );

  const csv = [header, ...lines].map((line) => line.join(",")).join("\r\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
  triggerDownload(blob, fileName);
};

/**
 * Excel real. La librería (`xlsx`) se carga con `import()` dinámico y solo al
 * descargar: no tiene sentido meterla en el bundle inicial del chat.
 */
export const downloadXlsx = async (
  dataset: DatasetPayload,
  fileName: string,
): Promise<void> => {
  const XLSX = await import("xlsx");

  const rows = dataset.rows.map((row) =>
    Object.fromEntries(
      dataset.columns.map((column) => [
        column.label,
        cellOf(row[column.key] ?? null),
      ]),
    ),
  );

  const sheet = XLSX.utils.json_to_sheet(rows, {
    header: dataset.columns.map((column) => column.label),
  });
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, "Datos");

  const buffer = XLSX.write(book, {
    type: "array",
    bookType: "xlsx",
  }) as ArrayBuffer;
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  triggerDownload(blob, fileName);
};
